import axios from "axios";

const server = process.env.REACT_APP_BACKEND || "http://localhost:2391";

function convertLegacyDataFormat(legacyData, deviceId) {
  if (!legacyData || (Array.isArray(legacyData) && legacyData.length === 0)) {
    console.warn(`設備 ${deviceId} 沒有數據，返回預設結構`);
    return {
      info: {
        device_id: deviceId,
        status: false,
        sensors: [
          { id: 1, name: "", value: "--" },
          { id: 2, name: "", value: "--" },
          { id: 3, name: "", value: "--" },
          { id: 4, name: "", value: "--" },
          { id: 5, name: "", value: "--" },
          { id: 6, name: "", value: "--" },
          { id: 7, name: "", value: "--" },
          { id: 8, name: "", value: "--" },
          { id: 9, name: "", value: "--" }
        ],
        last_update: null,
        show_heat_risk: 0
      }
    };
  }

  const latestData = Array.isArray(legacyData) ? legacyData[0] : legacyData;
  if (!latestData) {
    return {
      info: {
        device_id: deviceId,
        status: false,
        sensors: [
          { id: 1, name: "", value: "--" },
          { id: 2, name: "", value: "--" },
          { id: 3, name: "", value: "--" },
          { id: 4, name: "", value: "--" },
          { id: 5, name: "", value: "--" },
          { id: 6, name: "", value: "--" },
          { id: 7, name: "", value: "--" },
          { id: 8, name: "", value: "--" },
          { id: 9, name: "", value: "--" }
        ],
        last_update: null,
        show_heat_risk: 0
      }
    };
  }

  const deviceStatus = checkDeviceOnlineStatus(latestData);

  const safeValue = (value) => {
    if (value === null || value === undefined || value === '') {
      return "--";
    }
    const num = Number(value);
    return Number.isNaN(num) ? "--" : num;
  };

  const safeName = (name) => {
    if (name === null || name === undefined) {
      return "";
    }
    const trimmed = String(name).trim();
    return trimmed;
  };

  const sensors = [
    { id: 1, name: safeName(latestData.a0), value: safeValue(latestData.a_val) },
    { id: 2, name: safeName(latestData.b0), value: safeValue(latestData.b_val) },
    { id: 3, name: safeName(latestData.c0), value: safeValue(latestData.c_val) },
    { id: 4, name: safeName(latestData.d0), value: safeValue(latestData.d_val) },
    { id: 5, name: safeName(latestData.e0), value: safeValue(latestData.e_val) },
    { id: 6, name: safeName(latestData.f0), value: safeValue(latestData.f_val) },
    { id: 7, name: safeName(latestData.g0), value: safeValue(latestData.g_val) },
    { id: 8, name: safeName(latestData.h0), value: safeValue(latestData.h_val) },
    { id: 9, name: latestData.show_heat_risk === 1 ? "熱指數" : "", value: latestData.show_heat_risk === 1 ? safeValue(latestData.heat_index) : "--" }
  ];

  return {
    info: {
      device_id: deviceId,
      status: deviceStatus,
      sensors,
      level: latestData.level ?? 5,
      last_update: latestData.time || new Date().toISOString(),
      show_heat_risk: latestData.show_heat_risk ?? 0
    }
  };
}

function checkDeviceOnlineStatus(latestData) {
  if (!latestData || !latestData.time) {
    return false;
  }
  
  try {
    let lastTime;
    if (latestData.time instanceof Date) {
      lastTime = latestData.time;
    } else {
      const timeStr = String(latestData.time);
      try {
        lastTime = new Date(timeStr);
      } catch (e) {
        try {
          lastTime = new Date(timeStr.replace('T', ' ').replace('Z', ''));
        } catch (e2) {
          console.warn('無法解析時間格式:', timeStr);
          return false;
        }
      }
    }
    
    if (isNaN(lastTime.getTime())) {
      console.warn('時間格式無效:', latestData.time);
      return false;
    }
    
    const now = new Date();
    const timeDiff = now - lastTime;
    
    const isOnline = timeDiff < (10 * 60 * 1000);
    
    console.log(`設備狀態檢查 - 最新數據時間: ${lastTime.toISOString()}, 時間差: ${Math.round(timeDiff/1000)}秒, 狀態: ${isOnline ? '在線' : '離線'}`);
    
    return isOnline;
    
  } catch (error) {
    console.error('檢查設備在線狀態失敗:', error);
    return false;
  }
}

function convertLegacyChartFormat(legacyData) {
  if (!Array.isArray(legacyData) || legacyData.length === 0) {
    return [];
  }
  
  return legacyData.map(item => ({
    time: item.time,
    timestamp: new Date(item.time).getTime(),
    sensor1: item.a_val ?? null,
    sensor2: item.b_val ?? null,
    sensor3: item.c_val ?? null,
    sensor4: item.d_val ?? null,
    sensor5: item.e_val ?? null,
    sensor6: item.f_val ?? null,
    sensor7: item.g_val ?? null,
    sensor8: item.h_val ?? null,
    sensor9: item.heat_index ?? null,
    a_val: item.a_val ?? null,
    b_val: item.b_val ?? null,
    c_val: item.c_val ?? null,
    d_val: item.d_val ?? null,
    e_val: item.e_val ?? null,
    f_val: item.f_val ?? null,
    g_val: item.g_val ?? null,
    h_val: item.h_val ?? null,
    heat_index: item.heat_index ?? null
  }));
}

const GAS_API_BASE = `${server}/api/gas`;
const LEGACY_API_BASE = `${server}/gasmonitor`;

const Gas = {
  fetchGasData: async (deviceId) => {
    try {
      const response = await axios.get(`${GAS_API_BASE}/${deviceId}/data`);
      if (response.data.code === 200) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || '獲取數據失敗');
      }
    } catch (error) {
      console.error(`獲取設備 ${deviceId} 數據失敗:`, error);
      try {
        const fallbackResponse = await axios.get(`${LEGACY_API_BASE}/data?id=${deviceId}`);
        if (fallbackResponse.data.code === 200) {
          return convertLegacyDataFormat(fallbackResponse.data.data, deviceId);
        } else if (fallbackResponse.data.code === 500) {
          console.warn(`設備 ${deviceId} 後端返回 500，可能沒有數據`);
          return convertLegacyDataFormat([], deviceId);
        }
      } catch (fallbackError) {
        console.error('後備 API 也失敗:', fallbackError);
        if (fallbackError.response && fallbackError.response.status === 500) {
          console.warn(`設備 ${deviceId} 所有 API 都失敗，返回空數據結構`);
          return convertLegacyDataFormat([], deviceId);
        }
      }
      throw error;
    }
  },

  getChartData: async (deviceId, hours = 24) => {
    try {
      const response = await axios.get(`${GAS_API_BASE}/${deviceId}/history`, {
        params: { hours }
      });
      if (response.data.code === 200) {
        return { code: 200, data: response.data.data };
      } else {
        throw new Error(response.data.message || '獲取圖表數據失敗');
      }
    } catch (error) {
      console.error(`獲取設備 ${deviceId} 圖表數據失敗:`, error);
      try {
        const days = Math.ceil(hours / 24);
        const fallbackResponse = await axios.get(`${LEGACY_API_BASE}/data?id=${deviceId}&days=${days}`);
        if (fallbackResponse.data.code === 200) {
          return { code: 200, data: convertLegacyChartFormat(fallbackResponse.data.data) };
        } else {
          return { code: 200, data: [] };
        }
      } catch (fallbackError) {
        console.error('後備圖表 API 也失敗:', fallbackError);
        return { code: 200, data: [] };
      }
    }
  },

  getDeviceConfig: async (deviceId) => {
    try {
      const response = await axios.get(`${GAS_API_BASE}/${deviceId}/config`);
      if (response.data.code === 200) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || '獲取配置失敗');
      }
    } catch (error) {
      console.error(`獲取設備 ${deviceId} 配置失敗:`, error);
      try {
        const fallbackResponse = await axios.get(`${LEGACY_API_BASE}/device/info?id=${deviceId}`);
        if (fallbackResponse.data.code === 200) {
          const config = fallbackResponse.data.data.config;
          return {
            device_imei: config.device_imei,
            mqtt_topic: config.mqtt_topic,
            sensors: {
              a0: config.a0 === null ? null : (config.a0 || ""),
              b0: config.b0 === null ? null : (config.b0 || ""),
              c0: config.c0 === null ? null : (config.c0 || ""),
              d0: config.d0 === null ? null : (config.d0 || ""),
              e0: config.e0 === null ? null : (config.e0 || ""),
              f0: config.f0 === null ? null : (config.f0 || ""),
              g0: config.g0 === null ? null : (config.g0 || ""),
              h0: config.h0 === null ? null : (config.h0 || "")
            },
            calibration: {
              a1: config.a1, a2: config.a2, b1: config.b1, b2: config.b2,
              c1: config.c1, c2: config.c2, d1: config.d1, d2: config.d2,
              e1: config.e1, e2: config.e2, f1: config.f1, f2: config.f2,
              g1: config.g1, g2: config.g2, h1: config.h1, h2: config.h2
            }
          };
        }
      } catch (fallbackError) {
        console.warn('後備配置 API 也失敗:', fallbackError);
      }
      throw error;
    }
  },

  updateAllSensors: async (deviceId, sensorsConfig, systemConfig = {}) => {
    try {
      const updateData = { sensors: {}, system: {} };

      Object.keys(sensorsConfig).forEach(sensorKey => {
        const config = sensorsConfig[sensorKey];

        let sensorName;
        if (config.name === null || config.name === undefined) {
          sensorName = null;
        } else if (config.name === "") {
          sensorName = "";
        } else {
          sensorName = String(config.name);
        }
        
        updateData.sensors[sensorKey] = {
          name: sensorName,
          calibration: {
            k: config.calibration?.A || 1,
            b: config.calibration?.B || 0
          }
        };
      });

      if (systemConfig.topic) {
        updateData.mqtt_topic = systemConfig.topic;
      }
      if (systemConfig.device_imei) {
        updateData.device_imei = systemConfig.device_imei;
      }
      if (systemConfig.bounds) {
        updateData.system = {
          upper_bound: systemConfig.bounds.upperBound,
          lower_bound: systemConfig.bounds.lowerBound,
          tolerance: systemConfig.bounds.tolerance || 0,
          sensor: systemConfig.sensor || 'a'
        };
      }

      const response = await axios.put(`${GAS_API_BASE}/${deviceId}/config`, updateData);
      if (response.data.code === 200) {
        return response.data;
      } else {
        throw new Error(response.data.message || '更新配置失敗');
      }
    } catch (error) {
      console.error(`更新設備 ${deviceId} 配置失敗:`, error);
      
      try {
        const legacyData = { deviceID: deviceId };
        
        Object.keys(sensorsConfig).forEach(sensorKey => {
          const sensorIndex = sensorKey.replace('sensor', '');
          const sensorMap = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];
          const sensorChar = sensorMap[parseInt(sensorIndex) - 1];
          
          if (sensorChar) {
            const config = sensorsConfig[sensorKey];
            
            if (config.name === null || config.name === undefined) {
              legacyData[`${sensorChar}0`] = "";
            } else if (config.name === "") {
              legacyData[`${sensorChar}0`] = "";
            } else {
              const fieldName = String(config.name).trim();
              if (fieldName && !/^[a-zA-Z0-9]+$/.test(fieldName)) {
                throw new Error(`感測器名稱 '${fieldName}' 只能包含英文和數字`);
              }
              legacyData[`${sensorChar}0`] = fieldName;
            }
            
            legacyData[`${sensorChar}1`] = config.calibration?.A || 1;
            legacyData[`${sensorChar}2`] = config.calibration?.B || 0;
          }
        });

        if (systemConfig.topic) {
          legacyData.mqtt_topic = systemConfig.topic;
        }
        if (systemConfig.device_imei) {
          legacyData.device_imei = systemConfig.device_imei;
        }

        console.log('發送到後端的舊版 API 數據:', legacyData);

        const fallbackResponse = await axios.post(`${LEGACY_API_BASE}/data`, legacyData);
        if (fallbackResponse.status === 200) {
          return { code: 200, message: 'Update successfully' };
        }
      } catch (fallbackError) {
        console.error('後備更新 API 也失敗:', fallbackError);
      }
      
      throw error;
    }
  },

  getSystemStatus: async () => {
    try {
      const response = await axios.get(`${GAS_API_BASE}/system/status`);
      if (response.data.code === 200) {
        return {
          code: 200,
          data: {
            mqtt_connected: response.data.data.mqtt.connected,
            device_count: response.data.data.devices.total_count,
            online_devices: response.data.data.devices.online_count,
            recent_data_count: response.data.data.data.recent_data_count,
            messages_processed: response.data.data.mqtt.messages_processed,
            messages_failed: response.data.data.mqtt.messages_failed
          }
        };
      } else {
        throw new Error(response.data.message || '獲取系統狀態失敗');
      }
    } catch (error) {
      console.error('獲取系統狀態失敗:', error);
      try {
        const fallbackResponse = await axios.get(`${LEGACY_API_BASE}/system/status`);
        if (fallbackResponse.data.code === 200) {
          return fallbackResponse.data;
        }
      } catch (fallbackError) {
        console.warn('後備系統狀態 API 也失敗:', fallbackError);
      }
      
      return {
        code: 200,
        data: {
          mqtt_connected: false,
          device_count: 0,
          online_devices: 0,
          recent_data_count: 0,
          messages_processed: 0,
          messages_failed: 0
        }
      };
    }
  },

  getAllDevices: async () => {
    try {
      const response = await axios.get(`${GAS_API_BASE}/devices`);
      if (response.data.code === 200) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || '獲取設備列表失敗');
      }
    } catch (error) {
      console.error('獲取設備列表失敗:', error);
      try {
        const fallbackResponse = await axios.get(`${LEGACY_API_BASE}/devices/list`);
        if (fallbackResponse.data.code === 200) {
          return fallbackResponse.data.data;
        }
      } catch (fallbackError) {
        console.warn('後備設備列表 API 也失敗:', fallbackError);
      }
      return [];
    }
  },

  validateIMEI: async (imei, deviceId = null) => {
    try {
      const response = await axios.post(`${GAS_API_BASE}/imei/validate`, {
        imei: imei,
        deviceId: deviceId
      });
      return {
        valid: response.status === 200,
        message: response.data.message || '驗證通過'
      };
    } catch (error) {
      console.error('IMEI 驗證失敗:', error);
      if (error.response && error.response.status === 409) {
        return {
          valid: false,
          message: error.response.data.message || 'IMEI 已被使用',
          conflictDevice: error.response.data.conflictDevice
        };
      }
      return {
        valid: false,
        message: error.response?.data?.message || 'IMEI 驗證失敗'
      };
    }
  },

  searchByIMEI: async (imei) => {
    try {
      const response = await axios.get(`${LEGACY_API_BASE}/imei/search?imei=${encodeURIComponent(imei)}`);
      if (response.data.code === 200) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'IMEI 未註冊');
      }
    } catch (error) {
      console.error('根據 IMEI 搜尋設備失敗:', error);
      if (error.response && error.response.status === 404) {
        throw new Error('IMEI 未註冊');
      }
      throw error;
    }
  },

  bindIMEI: async (deviceId, imei) => {
    try {
      const response = await axios.post(`${LEGACY_API_BASE}/imei/bind`, {
        deviceId: deviceId,
        imei: imei
      });
      if (response.data.code === 200) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || '綁定失敗');
      }
    } catch (error) {
      console.error('綁定 IMEI 失敗:', error);
      if (error.response && error.response.status === 409) {
        throw new Error(error.response.data.message || 'IMEI 已被其他設備使用');
      }
      throw error;
    }
  },

  unbindIMEI: async (deviceId) => {
    try {
      const response = await axios.post(`${LEGACY_API_BASE}/imei/unbind`, {
        deviceId: deviceId
      });
      if (response.data.code === 200) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || '解綁失敗');
      }
    } catch (error) {
      console.error('解綁 IMEI 失敗:', error);
      throw error;
    }
  },

  getChartDataByDateRange: async (deviceId, startDate, endDate) => {
    try {
      const response = await axios.get(`${GAS_API_BASE}/../gasmonitor/data/date-range`, {
        params: { 
          id: deviceId,
          start_date: startDate,
          end_date: endDate
        }
      });
      if (response.data.code === 200) {
        return { code: 200, data: response.data.data };
      } else {
        throw new Error(response.data.message || '獲取日期範圍資料失敗');
      }
    } catch (error) {
      console.error(`獲取設備 ${deviceId} 日期範圍資料失敗:`, error);
      return { code: 500, data: [] };
    }
  },

  getSensorBounds: async (deviceId, sensorKey) => {
    try {
      const response = await axios.get(`${GAS_API_BASE}/../gasmonitor/sensor/${sensorKey}/bounds`, {
        params: { device_id: deviceId }
      });
      if (response.data.code === 200) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || '獲取感測器邊界值失敗');
      }
    } catch (error) {
      console.error(`獲取設備 ${deviceId} 感測器 ${sensorKey} 邊界值失敗:`, error);
      throw error;
    }
  },

  updateSensorBounds: async (deviceId, sensorKey, bounds) => {
    try {
      const response = await axios.put(`${GAS_API_BASE}/../gasmonitor/sensor/${sensorKey}/bounds`, {
        device_id: deviceId,
        upper_bound: bounds.upperBound,
        lower_bound: bounds.lowerBound,
        tolerance: bounds.tolerance || 1
      });
      if (response.data.code === 200) {
        return response.data;
      } else {
        throw new Error(response.data.message || '更新感測器邊界值失敗');
      }
    } catch (error) {
      console.error(`更新設備 ${deviceId} 感測器 ${sensorKey} 邊界值失敗:`, error);
      throw error;
    }
  },

  updateDeviceConfig: async (deviceId, configData) => {
    try {
      const response = await axios.put(`${GAS_API_BASE}/${deviceId}/config`, configData);
      if (response.data.code === 200) {
        return response.data;
      } else {
        throw new Error(response.data.message || 'HTTP error!');
      }
    } catch (error) {
      console.error(`${deviceId} 更新設定失敗:`, error);
      throw error;
    }
  },

  getAlertConfig: async (deviceId) => {
    try {
      const response = await axios.get(`${GAS_API_BASE}/${deviceId}/alert-config`);
      if (response.data.code === 200) {
        return response.data.data;
      }
      throw new Error(response.data.message || '獲取警報設定失敗');
    } catch (error) {
      console.error(`獲取設備 ${deviceId} 警報設定失敗:`, error);
      throw error;
    }
  },

  updateAlertConfig: async (deviceId, alertConfig) => {
    try {
      const response = await axios.put(
        `${GAS_API_BASE}/${deviceId}/alert-config`,
        alertConfig
      );
      if (response.data.code === 200) {
        return response.data;
      }
      throw new Error(response.data.message || '更新警報設定失敗');
    } catch (error) {
      console.error(`更新設備 ${deviceId} 警報設定失敗:`, error);
      // 後端 400 會帶具體原因（例如「警報間隔需介於 1~1440 分鐘」），保留以便顯示給使用者
      throw new Error(
        error.response?.data?.message || error.message || '更新警報設定失敗'
      );
    }
  },
};

export default Gas;
export { Gas };